import React, { useState } from 'react';
import { Plus, Trash2, Calendar, Briefcase, GraduationCap, AlertCircle, Sparkles, X } from 'lucide-react';
import { DEBT_TYPES } from '../utils/financialEvents';

// Lifestyle Expenses Panel - Shows accepted lifestyle trade-offs
export const LifestyleExpensesPanel = ({ vision, onUpdate, darkMode }) => {
  const lifestyleExpenses = vision.lifestyleExpenses || [];

  const removeExpense = (id) => {
    onUpdate({
      lifestyleExpenses: lifestyleExpenses.filter(e => e.id !== id)
    });
  };

  if (lifestyleExpenses.length === 0) {
    return null; // Don't show if no lifestyle expenses
  }

  return (
    <div className={`rounded-xl border-2 shadow-md hover:shadow-lg transition-all p-3 space-y-2 ${
      darkMode ? 'bg-slate-800 border-sky-600 hover:border-sky-500' : 'bg-sky-50 border-sky-300 hover:border-sky-400'
    }`}>
      <div className="flex justify-between items-center">
        <h3 className={`text-sm font-semibold flex items-center gap-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          <Sparkles className={`w-4 h-4 ${darkMode ? 'text-sky-400' : 'text-sky-600'}`} />
          Lifestyle Choices
        </h3>
        <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {lifestyleExpenses.length} active
        </span>
      </div>

      <div className="space-y-2">
        {lifestyleExpenses.map(expense => (
          <div key={expense.id} className={`flex justify-between items-center p-2 rounded border ${
            expense.isIncomeBoost
              ? (darkMode ? 'bg-emerald-900/30 border-emerald-700' : 'bg-emerald-50 border-emerald-200')
              : (darkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-50 border-gray-200')
          }`}>
            <div className="flex-1 min-w-0">
              <div className={`text-xs font-medium truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>{expense.name}</div>
              <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {expense.isIncomeBoost ? (
                  <span className={darkMode ? 'text-emerald-400' : 'text-emerald-700'}>+${Math.abs(expense.monthlyCost)}/mo income</span>
                ) : (
                  <span className={darkMode ? 'text-red-400' : 'text-red-700'}>${expense.monthlyCost}/mo expense</span>
                )}
                {' '}from age {expense.startAge}{expense.endAge ? ` to ${expense.endAge}` : '+'}
              </div>
            </div>
            <button
              onClick={() => removeExpense(expense.id)}
              className={`p-1 rounded ml-2 flex-shrink-0 ${darkMode ? 'hover:bg-red-900/50' : 'hover:bg-red-50'}`}
              title="Remove lifestyle choice"
            >
              <Trash2 className="w-3 h-3 text-red-500" />
            </button>
          </div>
        ))}
      </div>

      <p className={`text-xs mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        These affect your monthly expenses/income from their start age onward.
      </p>
    </div>
  );
};

// Debt Management Component
export const DebtPanel = ({ vision, onUpdate, darkMode }) => {
  const [showAddDebt, setShowAddDebt] = useState(false);
  const debts = vision.debts || [];

  const addDebt = (debt) => {
    console.log('💳 Adding debt:', debt);
    console.log('💳 Current debts:', debts);
    const newDebts = [...debts, { id: Date.now(), ...debt }];
    console.log('💳 New debts array:', newDebts);
    onUpdate({
      debts: newDebts
    });
    setShowAddDebt(false);
  };

  const removeDebt = (id) => {
    onUpdate({
      debts: debts.filter(d => d.id !== id)
    });
  };

  return (
    <div className={`rounded-xl border-2 shadow-md hover:shadow-lg transition-all p-3 space-y-2 ${
      darkMode ? 'bg-slate-800 border-red-700 hover:border-red-600' : 'bg-red-50 border-red-300 hover:border-red-400'
    }`}>
      <div className="flex justify-between items-center">
        <h3 className={`text-sm font-semibold flex items-center gap-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          <AlertCircle className={`w-4 h-4 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
          Debts
        </h3>
        <button
          onClick={() => {
            console.log('🔴 Add Debt button clicked, opening modal');
            setShowAddDebt(true);
          }}
          className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          <Plus className="w-3 h-3 inline mr-1" />
          Add Debt
        </button>
      </div>

      {debts.length === 0 ? (
        <p className={`text-xs italic ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No debts tracked</p>
      ) : (
        <div className="space-y-2">
          {debts.map(debt => (
            <div key={debt.id} className={`flex justify-between items-center p-2 rounded border ${
              darkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-50 border-gray-200'
            }`}>
              <div>
                <div className={`text-xs font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{debt.name}</div>
                <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  ${debt.balance?.toLocaleString()} @ {(debt.interestRate * 100).toFixed(1)}% | ${debt.monthlyPayment}/mo
                </div>
              </div>
              <button
                onClick={() => removeDebt(debt.id)}
                className={`p-1 rounded ${darkMode ? 'hover:bg-red-900/50' : 'hover:bg-red-50'}`}
              >
                <Trash2 className="w-3 h-3 text-red-500" />
              </button>
            </div>
          ))}
        </div>
      )}

      {showAddDebt && (
        <AddDebtModal onClose={() => setShowAddDebt(false)} onAdd={addDebt} />
      )}
    </div>
  );
};

// One-Time Events Component
export const EventsPanel = ({ vision, onUpdate, darkMode }) => {
  const [showAddEvent, setShowAddEvent] = useState(false);
  const events = vision.oneTimeEvents || [];

  const addEvent = (event) => {
    onUpdate({
      oneTimeEvents: [...events, { id: Date.now(), ...event }]
    });
    setShowAddEvent(false);
  };

  const removeEvent = (id) => {
    onUpdate({
      oneTimeEvents: events.filter(e => e.id !== id)
    });
  };

  return (
    <div className={`rounded-xl border-2 shadow-md hover:shadow-lg transition-all p-3 space-y-2 ${
      darkMode ? 'bg-slate-800 border-teal-700 hover:border-teal-600' : 'bg-teal-50 border-teal-300 hover:border-teal-400'
    }`}>
      <div className="flex justify-between items-center">
        <h3 className={`text-sm font-semibold flex items-center gap-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          <Calendar className={`w-4 h-4 ${darkMode ? 'text-teal-400' : 'text-teal-600'}`} />
          One-Time Events
        </h3>
        <button
          onClick={() => setShowAddEvent(true)}
          className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          <Plus className="w-3 h-3 inline mr-1" />
          Add Event
        </button>
      </div>

      {events.length === 0 ? (
        <p className={`text-xs italic ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No events planned</p>
      ) : (
        <div className="space-y-2">
          {events.sort((a, b) => a.age - b.age).map(event => (
            <div key={event.id} className={`flex justify-between items-center p-2 rounded border ${
              darkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-50 border-gray-200'
            }`}>
              <div>
                <div className={`text-xs font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {event.name} (Age {event.age})
                </div>
                <div className={`text-xs ${event.amount > 0 ? (darkMode ? 'text-green-400' : 'text-green-600') : (darkMode ? 'text-red-400' : 'text-red-600')}`}>
                  ${Math.abs(event.amount).toLocaleString()}
                </div>
              </div>
              <button
                onClick={() => removeEvent(event.id)}
                className={`p-1 rounded ${darkMode ? 'hover:bg-red-900/50' : 'hover:bg-red-50'}`}
              >
                <Trash2 className="w-3 h-3 text-red-500" />
              </button>
            </div>
          ))}
        </div>
      )}

      {showAddEvent && (
        <AddEventModal onClose={() => setShowAddEvent(false)} onAdd={addEvent} currentAge={vision.age} />
      )}
    </div>
  );
};

// Career Timeline Component - Table-based income projection with overrides
export const CareerPanel = ({ vision, onUpdate, darkMode }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const incomeOverrides = vision.incomeOverrides || {};
  // Check both partnerIncome and partnerIncomeStart for partner detection
  const hasPartner = (vision.partnerIncome > 0) || (vision.partnerIncomeStart > 0) || vision.hasPartner;
  const partnerStartIncome = vision.partnerIncome || vision.partnerIncomeStart || 0;

  // Generate projected income for ALL years using the growth model
  const generateProjectedIncome = (startAge, retireAge, startIncome) => {
    const projections = [];
    let currentIncome = startIncome;

    for (let age = startAge; age < retireAge; age++) {
      projections.push({
        age,
        projectedIncome: Math.round(currentIncome)
      });

      // Calculate growth for next year
      let growthRate;
      if (age <= 30) {
        growthRate = 0.06; // Early career: 6%
      } else if (age <= 45) {
        growthRate = 0.04; // Mid career: 4%
      } else if (age <= 60) {
        growthRate = 0.02; // Late career: 2%
      } else {
        growthRate = 0.01; // Pre-retirement: 1%
      }

      currentIncome *= (1 + growthRate);
    }

    return projections;
  };

  const userAge = vision.age || 30;
  const userRetireAge = vision.retireAge || 65;
  const userIncome = vision.income || vision.currentIncome || 75000;

  const partnerAge = vision.partnerAge || userAge;
  const partnerRetireAge = vision.partnerRetireAge || userRetireAge;

  const userProjections = generateProjectedIncome(userAge, userRetireAge, userIncome);

  const partnerProjections = hasPartner && partnerStartIncome > 0
    ? generateProjectedIncome(partnerAge, partnerRetireAge, partnerStartIncome)
    : [];

  // Create unified rows for all working AND retirement years (show 10 years into retirement)
  const maxRetireAge = Math.max(userRetireAge, partnerRetireAge);
  const maxAge = Math.min(maxRetireAge + 10, 95); // Show 10 years of retirement or until 95
  const allAges = [];
  for (let age = userAge; age < maxAge; age++) {
    allAges.push(age);
  }

  const updateIncomeOverride = (age, field, value) => {
    const newOverrides = { ...incomeOverrides };
    if (!newOverrides[age]) {
      newOverrides[age] = {};
    }
    if (value === '' || value === null) {
      delete newOverrides[age][field];
      if (Object.keys(newOverrides[age]).length === 0) {
        delete newOverrides[age];
      }
    } else {
      newOverrides[age][field] = parseInt(value) || 0;
    }
    onUpdate({ incomeOverrides: newOverrides });
  };

  const clearOverride = (age, field) => {
    updateIncomeOverride(age, field, null);
  };

  const hasAnyOverrides = Object.keys(incomeOverrides).length > 0;

  // Determine which ages to show (collapsed = key ages, expanded = all)
  const keyAges = allAges.filter(age =>
    age === userAge ||
    age === userRetireAge - 1 ||
    age % 5 === 0 ||
    incomeOverrides[age]
  );
  const displayAges = isExpanded ? allAges : keyAges;

  return (
    <div className={`rounded-xl border-2 shadow-md hover:shadow-lg transition-all p-3 space-y-2 ${
      darkMode ? 'bg-slate-800 border-indigo-700 hover:border-indigo-600' : 'bg-blue-50 border-indigo-300 hover:border-indigo-400'
    }`}>
      <div className="flex justify-between items-center">
        <h3 className={`text-sm font-semibold flex items-center gap-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          <Briefcase className={`w-4 h-4 ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
          Income Projections
        </h3>
        <div className="flex gap-2">
          {hasAnyOverrides && (
            <button
              onClick={() => onUpdate({ incomeOverrides: {} })}
              className={`text-xs px-2 py-1 rounded ${darkMode ? 'text-gray-400 hover:text-red-400 hover:bg-red-900/50' : 'text-slate-600 hover:text-red-600 hover:bg-red-50'}`}
            >
              Reset All
            </button>
          )}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-xs px-2 py-1 text-indigo-600 hover:bg-indigo-100 rounded"
          >
            {isExpanded ? 'Collapse' : `Show All ${allAges.length} Years`}
          </button>
        </div>
      </div>

      <p className="text-xs text-slate-500 bg-slate-100 rounded px-2 py-1">
        Click any value to override. Higher income = more savings = earlier retirement.
      </p>

      {/* Income Table */}
      <div className={`overflow-y-auto ${isExpanded ? 'max-h-64' : ''}`}>
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-blue-50">
            <tr className="border-b border-indigo-200">
              <th className="text-left py-1 px-2 font-medium text-slate-600">Age</th>
              <th className="text-right py-1 px-2 font-medium text-slate-600">Your Income</th>
              {hasPartner && partnerStartIncome > 0 && (
                <th className="text-right py-1 px-2 font-medium text-slate-600">Partner Income</th>
              )}
            </tr>
          </thead>
          <tbody>
            {displayAges.map(age => {
              const userProj = userProjections.find(p => p.age === age);
              const partnerProj = partnerProjections.find(p => p.age === age);
              const override = incomeOverrides[age] || {};
              const userOverridden = override.income !== undefined;
              const partnerOverridden = override.partnerIncome !== undefined;

              const userRetired = age >= userRetireAge;
              const partnerRetired = age >= partnerRetireAge;

              // Visual separation between working and retirement years
              const isRetirementTransition = age === userRetireAge;
              const isRetirementYear = age >= userRetireAge;

              return (
                <tr key={age} className={`border-b ${
                  isRetirementTransition ? 'border-emerald-400 border-t-2' : 'border-slate-100'
                } hover:bg-indigo-50/50 ${
                  (userOverridden || partnerOverridden) ? 'bg-indigo-50/30' : ''
                } ${isRetirementYear ? 'bg-emerald-50/20' : ''}`}>
                  <td className="py-1 px-2 font-medium text-slate-700">
                    {age}
                    {age === userAge && <span className="text-indigo-500 ml-1">(now)</span>}
                    {age === userRetireAge - 1 && <span className="text-orange-500 ml-1">(last work)</span>}
                    {age === userRetireAge && <span className="text-emerald-600 ml-1 font-semibold">(retire!)</span>}
                  </td>
                  <td className="py-1 px-2 text-right">
                    {userRetired ? (
                      <div className="flex items-center justify-end gap-1">
                        {userOverridden && (
                          <button
                            onClick={() => clearOverride(age, 'income')}
                            className="text-slate-400 hover:text-red-500"
                            title="Reset to default"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                        <input
                          type="number"
                          value={userOverridden ? override.income : (vision.retirementIncome || 0)}
                          onChange={(e) => updateIncomeOverride(age, 'income', e.target.value)}
                          placeholder="Pension"
                          className={`w-20 text-right px-1 py-0.5 rounded border text-xs ${
                            userOverridden
                              ? 'border-emerald-400 bg-emerald-100 font-medium text-emerald-700'
                              : 'border-transparent bg-transparent text-slate-500 hover:border-slate-300'
                          } focus:outline-none focus:ring-1 focus:ring-emerald-500`}
                        />
                        {((userOverridden && override.income > 0) || (!userOverridden && vision.retirementIncome > 0)) && (
                          <span className="text-[10px] text-slate-400">pension</span>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center justify-end gap-1">
                        {userOverridden && (
                          <button
                            onClick={() => clearOverride(age, 'income')}
                            className="text-slate-400 hover:text-red-500"
                            title="Reset to projected"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                        <input
                          type="number"
                          value={userOverridden ? override.income : (userProj?.projectedIncome || '')}
                          onChange={(e) => updateIncomeOverride(age, 'income', e.target.value)}
                          className={`w-20 text-right px-1 py-0.5 rounded border text-xs ${
                            userOverridden
                              ? 'border-indigo-400 bg-indigo-100 font-medium text-indigo-700'
                              : 'border-transparent bg-transparent text-slate-600 hover:border-slate-300'
                          } focus:outline-none focus:ring-1 focus:ring-indigo-500`}
                        />
                      </div>
                    )}
                  </td>
                  {hasPartner && partnerStartIncome > 0 && (
                    <td className="py-1 px-2 text-right">
                      {partnerRetired ? (
                        <div className="flex items-center justify-end gap-1">
                          {partnerOverridden && (
                            <button
                              onClick={() => clearOverride(age, 'partnerIncome')}
                              className="text-slate-400 hover:text-red-500"
                              title="Reset to default"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          )}
                          <input
                            type="number"
                            value={partnerOverridden ? override.partnerIncome : 0}
                            onChange={(e) => updateIncomeOverride(age, 'partnerIncome', e.target.value)}
                            placeholder="Pension"
                            className={`w-20 text-right px-1 py-0.5 rounded border text-xs ${
                              partnerOverridden
                                ? 'border-emerald-400 bg-emerald-100 font-medium text-emerald-700'
                                : 'border-transparent bg-transparent text-slate-500 hover:border-slate-300'
                            } focus:outline-none focus:ring-1 focus:ring-emerald-500`}
                          />
                          {partnerOverridden && override.partnerIncome > 0 && (
                            <span className="text-[10px] text-slate-400">pension</span>
                          )}
                        </div>
                      ) : partnerProj ? (
                        <div className="flex items-center justify-end gap-1">
                          {partnerOverridden && (
                            <button
                              onClick={() => clearOverride(age, 'partnerIncome')}
                              className="text-slate-400 hover:text-red-500"
                              title="Reset to projected"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          )}
                          <input
                            type="number"
                            value={partnerOverridden ? override.partnerIncome : (partnerProj?.projectedIncome || '')}
                            onChange={(e) => updateIncomeOverride(age, 'partnerIncome', e.target.value)}
                            className={`w-20 text-right px-1 py-0.5 rounded border text-xs ${
                              partnerOverridden
                                ? 'border-indigo-400 bg-indigo-100 font-medium text-indigo-700'
                                : 'border-transparent bg-transparent text-slate-600 hover:border-slate-300'
                            } focus:outline-none focus:ring-1 focus:ring-indigo-500`}
                          />
                        </div>
                      ) : (
                        <span className="text-slate-300">-</span>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {hasAnyOverrides && (
        <p className="text-xs text-indigo-600 italic">
          {Object.keys(incomeOverrides).length} override(s) applied
        </p>
      )}
    </div>
  );
};

// College Savings Component
export const CollegeSavingsPanel = ({ vision, onUpdate, darkMode }) => {
  const collegeSavings = vision.collegeSavings || {
    enabled: false,
    monthlyContribution: 0,
    currentBalance: 0,
    costPerYear: 30000,
    targetCoveragePercent: 100  // New: What % of college costs to cover
  };

  const updateCollegeSavings = (updates) => {
    onUpdate({
      collegeSavings: { ...collegeSavings, ...updates }
    });
  };

  // Calculate required monthly contribution to meet target coverage
  const calculateRequiredContribution = () => {
    const numKids = vision.numKids || 0;
    const firstChildAtAge = vision.firstChildAtAge || 0;
    const currentAge = vision.currentAge || 30;
    const retireAge = vision.retireAge || 65;
    const currentBalance = collegeSavings.currentBalance || 0;
    const costPerYear = collegeSavings.costPerYear || 30000;
    const targetCoveragePercent = collegeSavings.targetCoveragePercent || 100;

    if (numKids === 0 || firstChildAtAge === 0) {
      return { requiredMonthly: 0, cannotAchieve: false, reason: 'no-kids' };
    }

    // Calculate years until first child starts college (child will be 18)
    const childCurrentAge = currentAge - firstChildAtAge;
    const yearsUntilCollege = Math.max(0, 18 - childCurrentAge);

    // If kids are already in college or past college age, can't save for it
    if (yearsUntilCollege === 0) {
      return { requiredMonthly: 0, cannotAchieve: true, reason: 'too-late' };
    }

    // Calculate how many years we can contribute (until retirement or college starts)
    const yearsToContribute = Math.min(yearsUntilCollege, retireAge - currentAge);

    if (yearsToContribute <= 0) {
      return { requiredMonthly: 0, cannotAchieve: true, reason: 'already-retired' };
    }

    // Assume 6% annual return on 529 investments
    const annualReturn = 0.06;
    const monthlyReturn = annualReturn / 12;

    // Calculate total college costs needed
    // Each child needs 4 years of college, spaced 2 years apart
    // Assume costs grow at 3% inflation per year
    const costInflation = 0.03;
    let totalCostNeeded = 0;

    for (let kid = 0; kid < numKids; kid++) {
      const yearsFromFirstChild = kid * 2; // Kids spaced 2 years apart
      const yearsFromNow = yearsUntilCollege + yearsFromFirstChild;

      // Calculate cost for each of the 4 years of college for this child
      for (let year = 0; year < 4; year++) {
        const yearFromNow = yearsFromNow + year;
        const inflatedCost = costPerYear * Math.pow(1 + costInflation, yearFromNow);
        totalCostNeeded += inflatedCost;
      }
    }

    // Apply target coverage percentage
    const targetAmount = totalCostNeeded * (targetCoveragePercent / 100);

    // Calculate future value of current balance
    const monthsToContribute = yearsToContribute * 12;
    const futureValueOfCurrent = currentBalance * Math.pow(1 + monthlyReturn, monthsToContribute);

    // Calculate required monthly contribution using future value of annuity formula
    // FV = P * [((1 + r)^n - 1) / r]
    // Solving for P: P = (FV - FV_current) / [((1 + r)^n - 1) / r]
    const futureValueNeeded = targetAmount - futureValueOfCurrent;

    if (futureValueNeeded <= 0) {
      // Already have enough saved
      return { requiredMonthly: 0, cannotAchieve: false, reason: 'already-funded' };
    }

    const fvFactor = (Math.pow(1 + monthlyReturn, monthsToContribute) - 1) / monthlyReturn;
    const requiredMonthly = Math.ceil(futureValueNeeded / fvFactor);

    return {
      requiredMonthly,
      cannotAchieve: false,
      totalCostNeeded,
      targetAmount,
      yearsUntilCollege,
      yearsToContribute,
      futureValueOfCurrent
    };
  };

  const contributionCalc = calculateRequiredContribution();

  // Calculate college funding sufficiency (for display)
  const calculateCollegeFunding = () => {
    if (!collegeSavings.enabled) return null;

    const numKids = vision.numKids || 0;
    const firstChildAtAge = vision.firstChildAtAge || 0;

    if (numKids === 0 || firstChildAtAge === 0) {
      return { status: 'info', message: 'Set number of kids and first child age to calculate' };
    }

    if (contributionCalc.cannotAchieve) {
      if (contributionCalc.reason === 'too-late') {
        return { status: 'error', message: '⚠️ Kids already in/past college age' };
      } else if (contributionCalc.reason === 'already-retired') {
        return { status: 'error', message: '⚠️ Retirement age before college starts' };
      }
    }

    if (contributionCalc.reason === 'already-funded') {
      return {
        status: 'success',
        message: '✓ Fully Funded - No additional contributions needed!',
        yearsUntilCollege: contributionCalc.yearsUntilCollege
      };
    }

    const targetCoveragePercent = collegeSavings.targetCoveragePercent || 100;

    return {
      status: 'info',
      message: `Targeting ${targetCoveragePercent}% coverage - $${contributionCalc.requiredMonthly.toLocaleString()}/month needed`,
      requiredMonthly: contributionCalc.requiredMonthly,
      totalCostNeeded: Math.round(contributionCalc.totalCostNeeded),
      targetAmount: Math.round(contributionCalc.targetAmount),
      yearsUntilCollege: contributionCalc.yearsUntilCollege,
      yearsToContribute: contributionCalc.yearsToContribute
    };
  };

  const fundingStatus = calculateCollegeFunding();

  return (
    <div className={`rounded-xl border-2 shadow-md hover:shadow-lg transition-all p-3 space-y-2 ${
      darkMode ? 'bg-slate-800 border-green-700 hover:border-green-600' : 'bg-green-50 border-green-300 hover:border-green-400'
    }`}>
      <div className="flex justify-between items-center">
        <h3 className={`text-sm font-semibold flex items-center gap-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          <GraduationCap className={`w-4 h-4 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
          College Savings (529)
        </h3>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={collegeSavings.enabled}
            onChange={(e) => updateCollegeSavings({ enabled: e.target.checked })}
            className="w-4 h-4"
          />
          <span className="text-xs font-medium">Enabled</span>
        </label>
      </div>

      {collegeSavings.enabled && (
        <div className="space-y-3">
          <div>
            <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Target Coverage: {collegeSavings.targetCoveragePercent || 100}%
            </label>
            <input
              type="range"
              min="0"
              max="100"
              step="5"
              value={collegeSavings.targetCoveragePercent || 100}
              onChange={(e) => updateCollegeSavings({ targetCoveragePercent: parseInt(e.target.value) })}
              className="w-full"
            />
            <div className="flex justify-between text-[10px] text-gray-500">
              <span>0%</span>
              <span>25%</span>
              <span>50%</span>
              <span>75%</span>
              <span>100%</span>
            </div>
          </div>

          <div>
            <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Current 529 Balance</label>
            <input
              type="number"
              value={collegeSavings.currentBalance}
              onChange={(e) => updateCollegeSavings({ currentBalance: parseInt(e.target.value) || 0 })}
              className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                darkMode ? 'bg-slate-700 border-slate-500 text-white' : 'bg-white border-gray-300 text-gray-900'
              }`}
            />
          </div>

          <div>
            <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Est. College Cost/Year</label>
            <input
              type="number"
              value={collegeSavings.costPerYear}
              onChange={(e) => updateCollegeSavings({ costPerYear: parseInt(e.target.value) || 30000 })}
              className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                darkMode ? 'bg-slate-700 border-slate-500 text-white' : 'bg-white border-gray-300 text-gray-900'
              }`}
            />
          </div>

          {/* Show calculated monthly contribution */}
          {fundingStatus && fundingStatus.requiredMonthly > 0 && (
            <div className={`rounded-lg border-2 p-3 ${
              darkMode ? 'bg-blue-900/30 border-blue-500' : 'bg-blue-50 border-blue-400'
            }`}>
              <div className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Required Monthly Contribution:
              </div>
              <div className={`text-2xl font-bold ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                ${contributionCalc.requiredMonthly.toLocaleString()}<span className="text-sm">/month</span>
              </div>
              <div className={`text-[10px] mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Contributing for {fundingStatus.yearsToContribute} years until {fundingStatus.yearsToContribute < fundingStatus.yearsUntilCollege ? 'retirement' : 'college'}
              </div>
            </div>
          )}

          {/* College Funding Summary */}
          {fundingStatus && fundingStatus.totalCostNeeded && (
            <div className={`rounded-lg border p-2 space-y-1 ${
              darkMode ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-100 border-gray-300'
            }`}>
              <div className={`text-[10px] space-y-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <div className="flex justify-between">
                  <span>Total Cost ({vision.numKids} kids × 4 years):</span>
                  <span className="font-semibold">${fundingStatus.totalCostNeeded.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>Target Amount ({collegeSavings.targetCoveragePercent || 100}%):</span>
                  <span className="font-semibold">${fundingStatus.targetAmount.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>Years until first child in college:</span>
                  <span className="font-semibold">{fundingStatus.yearsUntilCollege} years</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Modal Components
const AddDebtModal = ({ onClose, onAdd }) => {
  const [debt, setDebt] = useState({
    name: '',
    type: 'credit_card',
    balance: 5000,
    interestRate: 0.20,
    monthlyPayment: 150
  });
  const [error, setError] = useState('');

  const handleSubmit = () => {
    console.log('🔴 handleSubmit called with:', debt);
    if (!debt.name || debt.name.trim() === '') {
      setError('Please enter a debt name');
      return;
    }
    if (debt.balance <= 0) {
      setError('Balance must be greater than 0');
      return;
    }
    if (debt.monthlyPayment <= 0) {
      setError('Monthly payment must be greater than 0');
      return;
    }
    setError('');
    console.log('🔴 Calling onAdd with:', debt);
    onAdd(debt);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]" id="add-debt-modal">
      <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
        <h3 className="text-lg font-bold mb-4">Add Debt</h3>

        {error && (
          <div className="mb-4 p-2 bg-red-100 border border-red-300 text-red-700 rounded text-sm">
            {error}
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">Debt Name <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={debt.name}
              onChange={(e) => setDebt({...debt, name: e.target.value})}
              placeholder="e.g., Credit Card, Student Loan"
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Type</label>
            <select
              value={debt.type}
              onChange={(e) => {
                const type = e.target.value;
                const typical = DEBT_TYPES[type];
                setDebt({...debt, type, interestRate: typical.typical_rate});
              }}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="credit_card">Credit Card</option>
              <option value="student_loan">Student Loan</option>
              <option value="auto_loan">Auto Loan</option>
              <option value="personal_loan">Personal Loan</option>
              <option value="mortgage">Mortgage</option>
              <option value="heloc">HELOC</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Current Balance</label>
            <input
              type="number"
              value={debt.balance}
              onChange={(e) => setDebt({...debt, balance: parseInt(e.target.value) || 0})}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Interest Rate (%)</label>
            <input
              type="number"
              step="0.1"
              value={(debt.interestRate * 100).toFixed(1)}
              onChange={(e) => setDebt({...debt, interestRate: parseFloat(e.target.value) / 100 || 0})}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Monthly Payment</label>
            <input
              type="number"
              value={debt.monthlyPayment}
              onChange={(e) => setDebt({...debt, monthlyPayment: parseInt(e.target.value) || 0})}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
        </div>

        <div className="flex gap-2 mt-6">
          <button
            type="button"
            onClick={handleSubmit}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Add Debt
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const AddEventModal = ({ onClose, onAdd, currentAge }) => {
  const [event, setEvent] = useState({
    name: '',
    type: 'major_purchase',
    age: currentAge + 5,
    amount: 10000
  });

  const handleSubmit = () => {
    if (event.name && event.age && event.amount) {
      // Make sure expenses are negative
      const finalAmount = ['major_purchase', 'car_purchase', 'wedding', 'home_renovation', 'vacation'].includes(event.type)
        ? -Math.abs(event.amount)
        : Math.abs(event.amount);

      onAdd({ ...event, amount: finalAmount });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold mb-4">Add One-Time Event</h3>

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">Event Name</label>
            <input
              type="text"
              value={event.name}
              onChange={(e) => setEvent({...event, name: e.target.value})}
              placeholder="e.g., Wedding, New Car, Inheritance"
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Type</label>
            <select
              value={event.type}
              onChange={(e) => setEvent({...event, type: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <optgroup label="Income">
                <option value="windfall">Windfall</option>
                <option value="inheritance">Inheritance</option>
                <option value="bonus">Bonus</option>
              </optgroup>
              <optgroup label="Expenses">
                <option value="major_purchase">Major Purchase</option>
                <option value="car_purchase">Car Purchase</option>
                <option value="wedding">Wedding</option>
                <option value="home_renovation">Home Renovation</option>
              </optgroup>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Age When This Occurs</label>
            <input
              type="number"
              value={event.age}
              onChange={(e) => setEvent({...event, age: parseInt(e.target.value) || currentAge})}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Amount ($)</label>
            <input
              type="number"
              value={event.amount}
              onChange={(e) => setEvent({...event, amount: parseInt(e.target.value) || 0})}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
        </div>

        <div className="flex gap-2 mt-6">
          <button
            onClick={handleSubmit}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Add Event
          </button>
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// eslint-disable-next-line no-unused-vars
const AddCareerEventModal = ({ onClose, onAdd, currentAge }) => {
  const [event, setEvent] = useState({
    type: 'promotion',
    age: currentAge + 3,
    newIncome: 0,
    unemploymentIncome: 0
  });

  const handleSubmit = () => {
    if (event.age) {
      onAdd(event);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-bold mb-4">Add Career Event</h3>

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">Event Type</label>
            <select
              value={event.type}
              onChange={(e) => setEvent({...event, type: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="promotion">Promotion</option>
              <option value="job_change">Job Change</option>
              <option value="layoff">Layoff</option>
              <option value="career_break">Career Break</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Age When This Occurs</label>
            <input
              type="number"
              value={event.age}
              onChange={(e) => setEvent({...event, age: parseInt(e.target.value) || currentAge})}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>

          {(event.type === 'promotion' || event.type === 'job_change') && (
            <div>
              <label className="block text-sm font-medium mb-1">New Income</label>
              <input
                type="number"
                value={event.newIncome}
                onChange={(e) => setEvent({...event, newIncome: parseInt(e.target.value) || 0})}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
          )}

          {(event.type === 'layoff' || event.type === 'career_break') && (
            <div>
              <label className="block text-sm font-medium mb-1">Income During This Period</label>
              <input
                type="number"
                value={event.unemploymentIncome}
                onChange={(e) => setEvent({...event, unemploymentIncome: parseInt(e.target.value) || 0})}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="0 for no income"
              />
            </div>
          )}
        </div>

        <div className="flex gap-2 mt-6">
          <button
            onClick={handleSubmit}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Add Event
          </button>
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
